import React, { useState, useEffect } from "react";
import { Form, Upload, message, Switch, ConfigProvider, Flex, UploadProps, UploadFile, DatePicker } from "antd";
import { RcFile } from "antd/lib/upload";
import ChallengeProvider from "../../../../../../../shared/provider/ChallengeProvider";
import ChallengeDto from "../../../../../../../shared/dto/ChallengeDto";
import { AddModalContainer, AddModalForm } from "./styles";
import TextArea from "antd/es/input/TextArea";
import ImgCrop from "antd-img-crop";
import ptBR from "antd/lib/locale/pt_BR";
import beforeUploadImages from "../../../../../../../shared/util/BeforeUpload";
import { StyledSelect } from "../../../../../../ideia/page/styles";
import dayjs, { Dayjs } from "dayjs";
import CustomModal from "../../../../../../../shared/util/AntModal/CustomModal";
import CategoryProvider from "../../../../../../../shared/provider/CategoryProvider";
import { CategoryDto } from "../../../../../../../shared/dto/CategoryDto";
import { Option } from "antd/es/mentions";
import { CloudUploadOutlined } from "@ant-design/icons";

interface AddChallengeFormProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>;
}

const AddChallengeForm: React.FC<AddChallengeFormProps> = ({
  open,
  setOpen,
  isLoading,
  setLoading,
  setSuccessMessage,
}) => {
  const [image, setImage] = useState<RcFile | null>(null);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [challengeName, setChallengeName] = useState<string>("");
  const [shortDescription, setShortDescription] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [fileList, setFileList] = React.useState<UploadFile[]>([]);
  const [coverImageUrl, setCoverImageUrl] = useState<string>("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [featured, setFeatured] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(true);
  const [selectedCategory, setSelectedCategory] = useState<CategoryDto>({} as CategoryDto);
  const [categories, setCategories] = useState<CategoryDto[]>([] as CategoryDto[]);
  const categoryProvider = new CategoryProvider();

  useEffect(() => {
    if (fileList[0] && fileList[0].status === "done") {
      setCoverImageUrl(fileList[0].url as string);
    }
  }, [fileList]);

  useEffect(() => {
    fetchCategories();
  }, []);

  async function fetchCategories() {
    try {
      const response = await categoryProvider.getAll({ page: 1, itemsPerPage: 1000 });
      if (response && response.data.length) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error("Erro ao obter categorias:", error);
    }
  }

  function handleCategoryChange(value: number) {
    if (categories.length) {
      const category = categories.find((category) => Number(category.categoryId) === value);
      if (category) {
        setSelectedCategory(category);
      }
    }
  }

  const handleSubmit = async () => {
    if (!haveValuesChanged() || fileList.length === 0) {
      message.error("Preencha todos os campos obrigatórios");
      return;
    }
    if (endDate && startDate && endDate.isBefore(startDate)) {
      message.error("A data final não pode ser antes da data inicial");
      return;
    }
    setLoading(true);
    try {
      const challengeProvider = new ChallengeProvider();
      const challenge: ChallengeDto = {
        title: challengeName,
        shortDescription: shortDescription,
        description: description,
        startDate: startDate?.toISOString() || new Date().toISOString(),
        endDate: endDate?.toISOString() || new Date().toISOString(),
        category: selectedCategory,
        featured: featured,
        active: active,
      };
      const createdChallenge = await challengeProvider.create(challenge, fileList[0]);
      setImage(null);
      setSuccessMessage("Desafio criado com sucesso!");
      setSuccessModalVisible(true);
    } catch (error) {
      message.error("Erro ao criar desafio.");
    } finally {
      setLoading(false);
    }
  };

  const clearBeforeLeave = () => {
    setChallengeName("");
    setShortDescription("");
    setDescription("");
    setFileList([]);
    setStartDate(null);
    setEndDate(null);
    setFeatured(false);
    setSelectedCategory({} as CategoryDto);
    setActive(true);
  }

  const defaultValues = {
    challengeName: "",
    shortDescription: "",
    description: "",
    fileList: [] as UploadFile[],
    startDate: null as Dayjs | null,
    endDate: null as Dayjs | null,
    featured: false,
    selectedCategory: {} as CategoryDto,
    active: true,
  };

  const haveValuesChanged = () => {
    return (
      challengeName !== defaultValues.challengeName ||
      shortDescription !== defaultValues.shortDescription ||
      description !== defaultValues.description ||
      fileList.length !== defaultValues.fileList.length ||
      startDate !== defaultValues.startDate ||
      endDate !== defaultValues.endDate ||
      featured !== defaultValues.featured || 
      selectedCategory.categoryId !== defaultValues.selectedCategory.categoryId
    );
  };

  const handleClose = () => {
    if (haveValuesChanged()) {
      setWarningModalVisible(true);
    } else {
      setOpen(false);
    }
  };

  const closeAllModals = () => {
    setSuccessModalVisible(false);
    setWarningModalVisible(false);
    setOpen(false);
  };

  const beforeUpload = (file: File) => {
    return beforeUploadImages(file, setFileList);
  };

  const onChangeFileUpload: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    if (newFileList.length > fileList.length) return;
    setFileList(newFileList);
  };

  return (
    <AddModalContainer>
      <CustomModal
        title={<h2>Criar Desafio</h2>}
        visible={open}
        onCancel={handleClose}
        onConfirm={handleSubmit}
        confirmText="Salvar desafio"
        footer={null}
        showWarning={warningModalVisible}
        showSuccess={successModalVisible}
        onWarningConfirm={() => {
          clearBeforeLeave();
          setWarningModalVisible(false);
          setOpen(false);
        }}
        onWarningCancel={() => setWarningModalVisible(false)}
        onSuccessConfirm={closeAllModals}
      >
        <AddModalForm onSubmit={handleSubmit}>
          <Flex gap={6} style={{ marginBottom: 20, flexDirection: "column" }}>
            <label className={"input-label"} htmlFor="title">
              Título*:
            </label>
            <TextArea
              id="nome"
              showCount
              placeholder="Digite aqui"
              maxLength={140}
              value={challengeName}
              onChange={(e) => setChallengeName(e.target.value)}
              autoSize={{ minRows: 1, maxRows: 3 }}
            />
          </Flex>

          <Flex gap={30} style={{ marginBottom: 20 }}>
            <Flex gap={6} vertical style={{ flex: 3 }}>
              <label className={"input-label"} htmlFor="title">
                Imagem/Banner*:
              </label>
              <ImgCrop aspect={4 / 1} rotationSlider>
                <Upload
                  beforeUpload={beforeUpload}
                  listType="picture-card"
                  fileList={fileList}
                  onChange={onChangeFileUpload}
                  maxCount={1}
                >
                  {fileList[0] ? null : <span>
                    <CloudUploadOutlined style={{fontSize: 24}} /><br />
                    Clique ou arraste a imagem (1200x300)<br />
                    .jpg, .jpeg, .png
                  </span>}
                </Upload>
              </ImgCrop>
            </Flex>
            <div style={{ flex: 2, maxWidth: 500 }}>
              

              <Flex gap={6} vertical flex={1}>
                  <ConfigProvider locale={ptBR}>
                    <label className={"input-label"} htmlFor="dataInicio">
                      Data início*:
                    </label>
                    <DatePicker
                      className="date-picker"
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      placeholder="Data de Início"
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      disabledDate={(current) => current && current < dayjs().startOf('day')}
                    />
                    <label className={"input-label"} htmlFor="dataFim">
                      Data fim*:
                    </label>
                    <DatePicker
                      className="date-picker"
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      placeholder="Data de Fim"
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      disabledDate={(current) => startDate ? current && current < startDate.startOf('day') : false}
                    />
                  </ConfigProvider>
                </Flex>
            </div>
          </Flex>

          <Flex gap={6} style={{ marginBottom: 20, flexDirection: "column" }}>
            <label className={"input-label"} htmlFor="shortDescription">
              Resumo*:
            </label>
            <TextArea
              id="shortDescription"
              showCount
              placeholder="Digite aqui"
              maxLength={200}
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              autoSize={{ minRows: 1, maxRows: 3 }}
            />
          </Flex>
          <Flex gap={6} style={{ marginBottom: 20, flexDirection: "column" }}>
            <label className={"input-label"} htmlFor="description">
              Descrição*:
            </label>
            <TextArea
              id="description"
              showCount
              placeholder="Digite aqui"
              maxLength={1500}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              autoSize={{ minRows: 3 }}
            />
          </Flex>
          
          <Flex gap={6} style={{ marginBottom: 20, flexDirection: "column" }}>
          <label className={"input-label"} htmlFor="categoria">
              Categoria*:
          </label>
          <StyledSelect
              className="category-select"
              placeholder="Categoria"
              value={selectedCategory && selectedCategory.categoryId}
              onChange={(value) => handleCategoryChange(value as number)}
            >
              {categories.map((category) => (
                <Option key={category.categoryId} value={category.categoryId}>
                  {category.name}
                </Option>
              ))}
          </StyledSelect>
          </Flex>
          <Flex gap={45} style={{ margin: "2px 0 10px 0" }}>
            <div>
              <label className={"input-label"} htmlFor="featured">
                É destaque*:
              </label>
              <Flex gap={12}>
                <Switch checked={featured} onChange={(checked) => setFeatured(checked)} /> {featured ? "Ativo" : "Desativado"}
              </Flex>
            </div>
            <div>
              <label className={"input-label"} htmlFor="active">
                Desafio ativo*:
              </label>
              <Flex gap={12}>
                <Switch checked={active} onChange={(checked) => setActive(checked)} /> {active ? "Ativo" : "Desativado"}
              </Flex>
            </div>
          </Flex>
        </AddModalForm>
      </CustomModal>
    </AddModalContainer>
  );
};

export default AddChallengeForm;